/**
 * Centralized frontend environment variable validation.
 *
 * Validates every VITE_* var the bundle reads at module load. Throws a
 * single, readable error in dev if anything is missing or malformed --
 * preferable to a runtime crash three components deep when a fetch hits
 * `undefined`. In production builds Vite inlines `import.meta.env.*` at
 * build time, so validation runs once on app boot.
 *
 * Prefer importing `env` from this file over reading `import.meta.env`
 * directly so callers get typed values with defaults already applied.
 *
 * Mirrors apps/backend/src/config/env.ts in spirit (Joi -> Zod because
 * the frontend already ships zod).
 */

import { z } from "zod"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Booleans in .env files arrive as strings. Accept "true"/"false" and the
// empty string (treated as the default).
const zBoolish = (defaultValue: boolean) =>
  z
    .union([z.literal("true"), z.literal("false"), z.literal("")])
    .default(defaultValue ? "true" : "false")
    .transform(v => (v === "" ? defaultValue : v === "true"))

// Sentry sample rate is parsed lazily because an empty string should fall
// back to the default rather than fail Number coercion.
const zSampleRate = (defaultValue: number) =>
  z
    .string()
    .default(String(defaultValue))
    .transform(v => (v === "" ? defaultValue : Number(v)))
    .pipe(z.number().min(0).max(1))

// Required-in-prod URL: defaulted in dev for ergonomics, required when
// the bundle is built for production (MODE=production OR staging). Matches
// the backend env.ts pattern of `Joi.when(NODE_ENV, ...)`.
const zRequiredInProdUrl = (devDefault: string) =>
  z
    .string()
    .default("")
    .superRefine((value, ctx) => {
      const mode = (import.meta.env.MODE as string) ?? "development"
      const isProdLike = mode === "production" || mode === "staging"
      if (isProdLike && value === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `required in ${mode} builds; set it in the deploy env`,
        })
        return
      }
      try {
        new URL(value === "" ? devDefault : value)
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "must be a valid URL" })
      }
    })
    .transform(value => (value === "" ? devDefault : value))

// Asset CDN base URL: callers concatenate it as a prefix (e.g.
// `${VITE_ASSET_BASE_URL}icon.svg`), so a value without a trailing slash
// produces broken URLs like `.../imagesadd_person.svg`. Normalize.
const zAssetBaseUrl = (devDefault: string) =>
  zRequiredInProdUrl(devDefault).transform(value => (value.endsWith("/") ? value : `${value}/`))

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const schema = z.object({
  // -- Vite-provided ------------------------------------------------------
  MODE: z.enum(["development", "test", "staging", "production"]).default("development"),

  // -- API ----------------------------------------------------------------
  // Required in production / staging; dev falls back to localhost.
  VITE_API_URL: zRequiredInProdUrl("http://localhost:3000"),

  // -- App URLs (used in share links and referral copy) -------------------
  VITE_APP_URL: zRequiredInProdUrl("http://localhost:5173"),
  VITE_APP_REFERRAL_URL: zRequiredInProdUrl("http://localhost:5173/ref"),

  // -- Asset CDN ----------------------------------------------------------
  // Required in prod; dev defaults to the addval CDN. Trailing slash is
  // normalized so call sites can safely do `${env.VITE_ASSET_BASE_URL}name`.
  VITE_ASSET_BASE_URL: zAssetBaseUrl("https://rituality-assets.addvalsolutions.com/images/"),

  // -- Google Maps (optional; location autocomplete degrades without it) -
  VITE_MAP_KEYS: z.string().default(""),

  // -- Sentry (optional) --------------------------------------------------
  VITE_SENTRY_DSN: z.string().default(""),
  VITE_SENTRY_TRACES_SAMPLE_RATE: zSampleRate(0.1),
  VITE_SENTRY_SEND_DEFAULT_PII: zBoolish(false),

  // -- Mixpanel (optional) ------------------------------------------------
  VITE_MIXPANEL_TOKEN: z.string().default(""),
})

// ---------------------------------------------------------------------------
// Validate
// ---------------------------------------------------------------------------

const parsed = schema.safeParse(import.meta.env)

if (!parsed.success) {
  const issues = parsed.error.issues
    .map(i => `  - ${i.path.join(".") || "(root)"}: ${i.message}`)
    .join("\n")
  throw new Error(`Frontend env validation failed:\n${issues}`)
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export type FrontendEnv = z.infer<typeof schema>
export const env: FrontendEnv = parsed.data
